import { Button, Divider, Flex, Modal, Space, Table } from "antd";
import Column from "antd/es/table/Column";
// import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { useCallback, useContext, useEffect, useState } from "react";
import { getAuthHeaders, PATH_INFO } from "../../util/helper";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { UpdateDeviceContext } from "../../../context/deviceContext";

type deviceType = {
  id: string,
  api_key: string,
  name: string,
  location: string,
  company: { company_name: string },
  created_at: string,
  updated_at: string,
  // initial_setup: boolean
}
type DeviceListResponse = {
  data: deviceType[],
  pagination: {
    page: number,
    pageSize: number,
    total: number
  }
}

const DEFAULT_PAGE_SIZE = 8


export function DeviceList() {
  const navigate = useNavigate()
  const { deleteDevice } = useContext(UpdateDeviceContext)

  const [deviceList, setDeviceList] = useState<deviceType[]>([])
  const [pagination, setPagination] = useState({ current: 1, pageSize: DEFAULT_PAGE_SIZE, total: 0 })
  const [loading, setLoading] = useState(false)

  const refreshDevices = useCallback(async (page = 1) => {
    setLoading(true)
    try {
      const query = new URLSearchParams({
        page: page.toString(),
        pageSize: pagination.pageSize.toString()
      })
      const headers = getAuthHeaders(navigate, {
        "Content-Type": 'application/json',
      })
      if (!headers) {
        return
      }

      const res = await fetch(`${process.env.REACT_APP_API_ENDPOINT}/apis/admin/devices?${query.toString()}`, {
        headers,
      })
      if (res.status === 401) {
        sessionStorage.removeItem('token')
        navigate(PATH_INFO.LOGIN.PATH)
        return
      }

      if (!res.ok) {
        throw new Error("Network response was not ok")
      }

      const data: DeviceListResponse = await res.json()
      setDeviceList(Array.isArray(data?.data) ? data.data : [])
      const paginationInfo = data?.pagination
      setPagination(prev => ({
        current: typeof paginationInfo?.page === 'number' ? paginationInfo.page : page,
        pageSize: typeof paginationInfo?.pageSize === 'number' ? paginationInfo.pageSize : prev.pageSize,
        total: typeof paginationInfo?.total === 'number' ? paginationInfo.total : prev.total
      }))
    } catch (error) {
      console.error("Error fetching devices.")
    } finally {
      setLoading(false)
    }
  }, [navigate, pagination.pageSize])

  useEffect(() => { refreshDevices() }, [refreshDevices])

  const handlePaginationChange = useCallback((page: number) => {
    setPagination(prev => ({ ...prev, current: page }))
    refreshDevices(page)
  }, [refreshDevices])

  const onClickManage = useCallback((id: string) => {
    const result = PATH_INFO.DEVICE_MANAGE.PATH.replace(':deviceId', id)
    navigate(result)
  }, [navigate])

  const onClickDelete = useCallback(async (id: string) => {
    const res = await deleteDevice(id)
    if (res) {
      Modal.success({ content: 'デバイスの削除が完了しました！', centered: true })
      const nextPage = pagination.current > 1 && deviceList.length === 1 ? pagination.current - 1 : pagination.current
      await refreshDevices(nextPage)
    } else {
      Modal.error({ content: '処理中にエラーが発生しました。', centered: true })
    }
  }, [deleteDevice, deviceList.length, pagination.current, refreshDevices])

  return (
    <div>
      <div className="flex w-full space-x-5 items-center">
        <p className="flex text-2xl decoration-solid ml-2 mt-0 mb-0 border-0 border-l-4 border-solid border-[#1677ff] p-2">デバイス一覧画面</p>
      </div>
      <Divider />
      <Flex vertical justify="center" className="relative">
        <Table<deviceType>
          dataSource={deviceList}
          bordered
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            position: ['bottomCenter'],
            showSizeChanger: false,
            onChange: handlePaginationChange
          }}
          rowKey={(record) => record.id}
          scroll={{ x: 'max-content' }}
        >
          <Column
            title='初期設定'
            dataIndex='initial_settings'
            key='initial_settings'
            render={(_: any, record: deviceType) => (
              // record.initial_settings === true ? (<CheckCircleOutlined className="text-green-500 text-xl" />) : (<CloseCircleOutlined className="text-red-500 text-xl" />)
              <></>
            )}
            className="text-center"
            width="90px"
          />
          <Column title='企業' dataIndex={['company', 'company_name']} key={'company_name'} />
          <Column title='拠点' dataIndex='location' key='location' />
          <Column title='デバイス名' dataIndex='name' key='name' />
          <Column title='API key' dataIndex='api_key' key='api_key' />
          <Column
            title='登録日'
            dataIndex='created_at'
            key='created_at'
            render={(el) => dayjs(el).format('YYYY-MM-DD')}
            sorter={(a, b) => dayjs(a.created_at).valueOf() - dayjs(b.created_at).valueOf()}
            sortDirections={['descend', 'ascend']}
          />
          <Column
            title='更新日'
            dataIndex='updated_at'
            key='updated_at'
            render={(el) => dayjs(el).format('YYYY-MM-DD')}
            sorter={(a, b) => dayjs(a.updated_at).valueOf() - dayjs(b.updated_at).valueOf()}
            sortDirections={['descend', 'ascend']}
          />
          <Column
            title='操作'
            dataIndex='action'
            key='action'
            fixed='right'
            render={(_: any, record: deviceType) => (
              <Space size='middle'>
                <Button variant='outlined' color='primary' onClick={() => onClickManage(record.id)}>編集</Button>
                <Button
                  variant='outlined'
                  color='danger'
                  onClick={() => {
                    Modal.warning({
                      title: `${record.name}を削除します`,
                      content: 'この操作は取り消せません。よろしいですか？',
                      onOk() { onClickDelete(record.id) },
                      okCancel: true,
                      centered: true,
                    })
                  }}
                >
                  削除
                </Button>
              </Space>
            )}
            width='175px'
          />
        </Table>
      </Flex>
      <Flex gap='small' className="justify-end">
        <Button type="primary" onClick={() => onClickManage('register')}>デバイスを新規登録</Button>
      </Flex>
    </div>
  )
}
